import type { LabelSheet, SupportedLocale, VariantLabel } from '@optiflow/types';
import { formatMoney } from '../money';
import { expandLabels } from './labelLines';
import { labelsPerPage, type LabelPreset } from './labelPresets';

/** The barcode as an image: an SVG in an `<img>` never runs a script, and the data URI needs no request. */
function svgSource(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/** One printed label: name, variant and its attributes, the barcode with its code, then SKU and price when asked. */
export function LabelFace({
  label,
  locale,
  preset,
}: {
  label: VariantLabel;
  locale: SupportedLocale;
  preset: LabelPreset;
}) {
  const name = locale === 'ar' ? (label.productNameAr ?? label.productName) : label.productName;
  const variant = [label.variantName, ...label.details].filter(Boolean).join(' · ');
  return (
    <div
      className="label-face"
      style={{ width: `${preset.label.width}mm`, height: `${preset.label.height}mm` }}
      data-testid="label-face"
    >
      <p className="label-face__name" dir="auto">
        {name}
      </p>
      {variant ? (
        <p className="label-face__variant" dir="auto">
          {variant}
        </p>
      ) : null}
      <img
        className="label-face__barcode"
        src={svgSource(label.barcode.svg)}
        alt={label.barcode.code}
      />
      <p className="label-face__code" dir="ltr">
        {label.barcode.code}
      </p>
      {label.sku || label.price ? (
        <p className="label-face__meta">
          {label.sku ? <span dir="ltr">{label.sku}</span> : null}
          {label.price ? (
            <span className="label-face__price">
              {formatMoney(label.price.amountMinor, locale, label.price.currencyCode)}
            </span>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Labels laid out page by page for a preset: one per page on a thermal roll, a grid on an A4 sheet.
 * `maxPages` limits a preview; printing always lays out every page.
 */
export function LabelSheetView({
  sheet,
  preset,
  locale,
  maxPages,
}: {
  sheet: LabelSheet;
  preset: LabelPreset;
  locale: SupportedLocale;
  maxPages?: number;
}) {
  const faces = expandLabels(sheet);
  const perPage = labelsPerPage(preset);
  const pages: VariantLabel[][] = [];
  for (let start = 0; start < faces.length; start += perPage) {
    pages.push(faces.slice(start, start + perPage));
  }
  const shown = maxPages === undefined ? pages : pages.slice(0, maxPages);

  return (
    <>
      {shown.map((page, pageIndex) => (
        <section
          key={pageIndex}
          className={`label-page label-page--${preset.id}`}
          data-testid="label-page"
          dir="ltr"
          style={{
            width: `${preset.page.width}mm`,
            height: `${preset.page.height}mm`,
            paddingTop: `${preset.margin.top}mm`,
            paddingInlineStart: `${preset.margin.left}mm`,
            gridTemplateColumns: `repeat(${preset.columns}, ${preset.label.width}mm)`,
            gridAutoRows: `${preset.label.height}mm`,
          }}
        >
          {page.map((label, index) => (
            <LabelFace
              key={`${label.variantId}-${index}`}
              label={label}
              locale={locale}
              preset={preset}
            />
          ))}
        </section>
      ))}
    </>
  );
}
